/**
 * Role → topics catalog. Keep in sync with
 * awarego-api/src/services/owaspTrainingDemo/roleTopics.js
 */

const OWASP = {
    A01: 'A01 - Broken Access Control',
    A02: 'A02 - Cryptographic Failures',
    A03: 'A03 - Injection (incl. SQL Injection)',
    A04: 'A04 - Insecure Design',
    A05: 'A05 - Security Misconfiguration',
    A06: 'A06 - Vulnerable and Outdated Components',
    A07: 'A07 - Identification and Authentication Failures',
    A08: 'A08 - Software and Data Integrity Failures',
    A09: 'A09 - Security Logging and Monitoring Failures',
    A10: 'A10 - Server-Side Request Forgery (SSRF)',
};

function topic(id, label, owaspKeys, focus) {
    const owasp = owaspKeys.map((k) => OWASP[k] || k);
    return { id, label, owasp, owaspKeys, focus };
}

const DEVELOPER_TOPICS = [
    'A01',
    'A02',
    'A03',
    'A04',
    'A05',
    'A06',
    'A07',
    'A08',
    'A09',
    'A10',
].map((k) =>
    topic(
        `dev-${k.toLowerCase()}`,
        OWASP[k],
        [k],
        `Technical secure-coding module for developers on ${OWASP[k]}. Include realistic code or app context where helpful.`
    )
);

export const ROLE_TOPICS = {
    'Software Developer': DEVELOPER_TOPICS,
    HR: [
        topic(
            'hr-hiring-security',
            'Assessing security competencies when hiring',
            ['A07', 'A05'],
            'How HR/recruiting evaluates security mindset and access practices during hiring.'
        ),
        topic(
            'hr-resume-red-flags',
            'Red flags in resumes (e.g. candidate from a compromised organization)',
            ['A05', 'A07'],
            'Due diligence and safe hiring when a candidate may have been exposed to a breach.'
        ),
        topic(
            'hr-interview-security-questions',
            'What to ask developers about security in interviews',
            ['A07', 'A04'],
            'HR-friendly interview questions that surface security awareness.'
        ),
        topic(
            'hr-phishing-entry-point',
            'Phishing and social engineering — how not to become the entry point',
            ['A07'],
            'HR as a high-value target for BEC and pretexting.'
        ),
    ],
    Manager: [
        topic(
            'mgr-security-debt-risk',
            'How security debt affects business risk',
            ['A01', 'A04'],
            'Business impact of access control and design flaws for managers.'
        ),
        topic(
            'mgr-ship-now-fix-later',
            'Why "release now, fix later" is a dangerous strategy',
            ['A04', 'A08'],
            'Trade-offs between speed and integrity.'
        ),
        topic(
            'mgr-read-security-reports',
            'How to read security reports from your team',
            ['A09', 'A01'],
            'Interpreting findings and severity without deep technical jargon.'
        ),
        topic(
            'mgr-prioritize-vulnerabilities',
            'Vulnerability prioritization: what to fix first and why',
            ['A01', 'A04', 'A09'],
            'Risk-based prioritization for leadership.'
        ),
    ],
    Finance: [
        topic(
            'fin-invoice-fraud',
            'Fraud through compromised systems (invoice fraud)',
            ['A08', 'A02'],
            'Invoice manipulation and payment process controls.'
        ),
        topic(
            'fin-pci-basics',
            'Protecting financial data — PCI DSS in plain language',
            ['A02', 'A07'],
            'Cardholder data basics for finance staff.'
        ),
        topic(
            'fin-bec-phishing',
            'Targeted phishing for finance staff (Business Email Compromise)',
            ['A07', 'A08'],
            'BEC tactics and verification procedures.'
        ),
        topic(
            'fin-ceo-fraud-urgent-transfer',
            'What to do if you receive an "urgent transfer from the CEO" request',
            ['A07', 'A08'],
            'Executive impersonation and out-of-band verification.'
        ),
    ],
    IT: [
        topic(
            'it-misconfiguration-a05',
            'Misconfiguration as a vulnerability (OWASP A05)',
            ['A05', 'A01'],
            'Hardening defaults and config mistakes for operators.'
        ),
        topic(
            'it-least-privilege',
            'Access management and least privilege',
            ['A01', 'A07'],
            'Accounts, roles, and privileged access.'
        ),
        topic(
            'it-patching',
            'Patching and updates — why they matter',
            ['A06', 'A05'],
            'Vulnerable components and patch cadence.'
        ),
        topic(
            'it-logging-monitoring-a09',
            'Logging and monitoring (OWASP A09)',
            ['A09', 'A01'],
            'Detection, logs, and alerting for IT operations.'
        ),
    ],
    Healthcare: [
        topic(
            'hc-phi-basics',
            'PHI and HIPAA-aware handling in plain language',
            ['A02', 'A01'],
            'Protecting patient data and minimum necessary access.'
        ),
        topic(
            'hc-phishing',
            'Phishing targeting healthcare staff',
            ['A07'],
            'Safe reporting without disrupting care.'
        ),
        topic(
            'hc-shared-workstations',
            'Shared workstations and access in clinical environments',
            ['A07', 'A05'],
            'Session lock and misconfiguration in clinical settings.'
        ),
        topic(
            'hc-third-party-vendors',
            'Third-party vendors and data sharing risks',
            ['A08', 'A04'],
            'Vendor compromise and verification before sharing PHI.'
        ),
    ],
};

export const ROLES = Object.keys(ROLE_TOPICS);

export const DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced'];

export const DEFAULT_ROLE = 'HR';
export const DEFAULT_DIFFICULTY = 'Intermediate';

export function getTopicsForRole(role) {
    return ROLE_TOPICS[role] || [];
}

export function getDefaultTopicIdForRole(role) {
    const topics = getTopicsForRole(role);
    return topics.length ? topics[0].id : '';
}

export function getTopicById(role, topicId) {
    return getTopicsForRole(role).find((t) => t.id === topicId) || null;
}

export const DEFAULT_TOPIC_ID = getDefaultTopicIdForRole(DEFAULT_ROLE);
